import React, { useState } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import ScopeTable from "./ScopeTable"; // Scope dialog component

export default function Fluke5522ACalculator({ calibrator }) {
  const [functionType, setFunctionType] = useState("");
  const [subFunction, setSubFunction] = useState("");
  const [amplitude, setAmplitude] = useState("");
  const [ampUnit, setAmpUnit] = useState("");
  const [frequency, setFrequency] = useState("");
  const [scopeValue, setScopeValue] = useState("");
  const [calculatedUncertainty, setCalculatedUncertainty] = useState("");
  const [calculatedUncertaintyUnit, setCalculatedUncertaintyUnit] = useState("");
  const [uncertaintyInRatio, setUncertaintyInRatio] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false); // Scope dialog

  const buttonWidth = "120px";
  const validateNumeric = (value) => /^-?(\d+)?(\.\d*)?$/.test(value);

  // Copy function with feedback (works even on http)
  const handleCopy = (value) => {
    if (!value) return;

    // Try the modern Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(value)
        .then(() => {
          console.log("Copied to clipboard!");
        })
        .catch((err) => {
          console.error("Clipboard write failed:", err);
        });
    } else {
      // Fallback for HTTP or older browsers
      const textArea = document.createElement("textarea");
      textArea.value = value;
      textArea.style.position = "fixed"; // Prevent scrolling
      textArea.style.opacity = 0;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy");
        console.log("Copied using fallback!");
      } catch (err) {
        console.error("Fallback copy failed:", err);
      }

      document.body.removeChild(textArea);
    }
  };

  const resetResults = () => {
    setScopeValue("");
    setCalculatedUncertainty("");
    setCalculatedUncertaintyUnit("");
    setUncertaintyInRatio("");
  };

  const handleCalculate = async () => {
    setErrorMessage("");
    setScopeValue("");
    setCalculatedUncertainty("");
    setCalculatedUncertaintyUnit("");
    setUncertaintyInRatio("");

    if (!validateNumeric(amplitude) || !validateNumeric(frequency)) {
      setErrorMessage(
        "Please enter valid numeric values for amplitude and frequency!"
      );
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        function: functionType || "",
        subFunction: subFunction || "",
        amplitudeUnit: ampUnit || "",
        amplitude: amplitude || "",
        frequency: frequency || "",
      });

      const response = await fetch(
        //`http://localhost:8080/api/calculator/calculate?${params.toString()}`
        `/api/calculator/calculate?${params.toString()}`
      );

      if (!response.ok) throw new Error("Server error: " + response.status);

      const data = await response.json();

      if (data.message) {
        setErrorMessage(data.message);
        return;
      }

      setScopeValue(data.scopeValue || "");
      setCalculatedUncertainty(data.calculatedUncertainty || "");
      setCalculatedUncertaintyUnit(ampUnit || "");
      setUncertaintyInRatio(data.uncertaintyInRatio || "");
    } catch (err) {
      setErrorMessage("Failed to fetch calculation: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontWeight: "bold",
            color: "primary.dark",
            textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
            mb: 2,
          }}
        >
          {calibrator} Uncertainty Calculator
        </Typography>

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        {/* Function */}
        <TextField
          select
          label="Function"
          value={functionType}
          onChange={(e) => {
            setFunctionType(e.target.value);
            resetResults();
          }}
          fullWidth
        >
          <MenuItem value="">(None)</MenuItem>
          <MenuItem value="Voltage DC">Voltage DC</MenuItem>
          <MenuItem value="Voltage AC">Voltage AC</MenuItem>
          <MenuItem value="Current DC">Current DC</MenuItem>
          <MenuItem value="Current AC">Current AC</MenuItem>
          <MenuItem value="Temperature">Temperature</MenuItem>
          <MenuItem value="Resistance">Resistance</MenuItem>
          <MenuItem value="Capacitance">Capacitance</MenuItem>
          <MenuItem value="RTD">RTD</MenuItem>
          <MenuItem value="Frequency">Frequency</MenuItem>
          <MenuItem value="Phase">Phase</MenuItem>
        </TextField>

        {/* Sub Function */}
        <TextField
          select
          label="Sub Function"
          value={subFunction}
          onChange={(e) => {
            setSubFunction(e.target.value);
            resetResults();
          }}
          fullWidth
        >
          <MenuItem value="">(None)</MenuItem>
          <MenuItem value="2-Wire">2-Wire</MenuItem>
          <MenuItem value="2-Wire Comp">2-Wire Comp</MenuItem>
          <MenuItem value="4-Wire Comp">4-Wire Comp</MenuItem>
          <MenuItem value="4-Wire Conn">4-Wire Conn</MenuItem>
          <MenuItem value="Type B T/C">Type B T/C</MenuItem>
          <MenuItem value="Type C T/C">Type C T/C</MenuItem>
          <MenuItem value="Type E T/C">Type E T/C</MenuItem>
          <MenuItem value="Type J T/C">Type J T/C</MenuItem>
          <MenuItem value="Type K T/C">Type K T/C</MenuItem>
          <MenuItem value="Type L T/C">Type L T/C</MenuItem>
          <MenuItem value="Type N T/C">Type N T/C</MenuItem>
          <MenuItem value="Type R T/C">Type R T/C</MenuItem>
          <MenuItem value="Type S T/C">Type S T/C</MenuItem>
          <MenuItem value="Type T T/C">Type T T/C</MenuItem>
          <MenuItem value="Type U T/C">Type U T/C</MenuItem>
          <MenuItem value="Cu 427, 10 Ohm">Cu 427, 10 Ohm</MenuItem>
          <MenuItem value="Pt 385, 100 Ohm">Pt 385, 100 Ohm</MenuItem>
          <MenuItem value="Pt 385, 1000 Ohm">Pt 385, 1000 Ohm</MenuItem>
          <MenuItem value="Pt 385, 200 Ohm">Pt 385, 200 Ohm</MenuItem>
          <MenuItem value="Pt 385, 500 Ohm">Pt 385, 500 Ohm</MenuItem>
          <MenuItem value="Pt 3916, 100 Ohm">Pt 3916, 100 Ohm</MenuItem>
          <MenuItem value="Pt 3926, 100 Ohm">Pt 3926, 100 Ohm</MenuItem>
          <MenuItem value="PtNi 385, 120 Ohm (Ni 120)">
            PtNi 385, 120 Ohm (Ni 120)
          </MenuItem>
        </TextField>

        {/* Scope Button */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{ width: buttonWidth }}
            onClick={() => setScopeOpen(true)}
          >
            Show Scope
          </Button>
        </Box>

        {/* Amplitude & Unit */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Amplitude"
            value={amplitude}
            onChange={(e) => {
              setAmplitude(e.target.value);
              resetResults();
            }}
            fullWidth
          />
          <TextField
            select
            label="Unit"
            value={ampUnit}
            onChange={(e) => {
              setAmpUnit(e.target.value);
              resetResults();
            }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="mV">mV</MenuItem>
            <MenuItem value="V">V</MenuItem>
            <MenuItem value="mVrms">mVrms</MenuItem>
            <MenuItem value="Vrms">Vrms</MenuItem>
            <MenuItem value="µA">µA</MenuItem>
            <MenuItem value="mA">mA</MenuItem>
            <MenuItem value="A">A</MenuItem>
            <MenuItem value="°C">°C</MenuItem>
            <MenuItem value="°F">°F</MenuItem>
            <MenuItem value="Ohm">Ohm</MenuItem>
            <MenuItem value="kOhm">kOhm</MenuItem>
            <MenuItem value="MOhm">MOhm</MenuItem>
            <MenuItem value="pF">pF</MenuItem>
            <MenuItem value="nF">nF</MenuItem>
            <MenuItem value="µF">µF</MenuItem>
            <MenuItem value="mF">mF</MenuItem>
            <MenuItem value="Hz">Hz</MenuItem>
            <MenuItem value="kHz">kHz</MenuItem>
            <MenuItem value="MHz">MHz</MenuItem>
            <MenuItem value="°">°</MenuItem>
          </TextField>
        </Box>

        {/* Frequency */}
        <TextField
          label="Frequency (Hz)"
          value={frequency}
          onChange={(e) => {
            setFrequency(e.target.value);
            resetResults();
          }}
          fullWidth
        />

        {/* Calculate button */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{ width: buttonWidth }}
            onClick={() => {
              setCalculatedUncertaintyUnit(ampUnit);
              handleCalculate();
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Calculate"}
          </Button>
        </Box>

        {/* Results */}
        <Stack spacing={1}>
          <Typography>Allowed Scope Value: {scopeValue}</Typography>

          <Typography>
            Calculated Uncertainty: {calculatedUncertainty}{" "}
            {calculatedUncertaintyUnit}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => handleCopy(`${calculatedUncertainty}`)}
            >
              Copy
            </Button>
          </Box>

          <Typography>Uncertainty in Ratio: {uncertaintyInRatio}</Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => handleCopy(`${uncertaintyInRatio}`)}
            >
              Copy
            </Button>
          </Box>
        </Stack>
      </Stack>

      {/* Scope Table Dialog */}
      <ScopeTable
        open={scopeOpen}
        onClose={() => setScopeOpen(false)}
        functionType={functionType}
        subFunction={subFunction}
      />
    </Paper>
  );
}
